import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import ActiveCallOverlay from '@/components/ActiveCallOverlay/ActiveCallOverlay';
import Fallback from '@/components/Fallback';
import AppContext, { type AppContextInterface } from '@/contexts/appContext';
import { CardOrderProvider } from '@/contexts/cardOrderContext';
import { InternalMixpanelProvider } from '@/contexts/internalMixpanelContext';
import { NarrowNavProvider } from '@/contexts/narrowNavContext';
import { PageAnchorsProvider } from '@/contexts/pageAnchorsContext';
import { SidebarLayoutProvider } from '@/contexts/sidebarLayoutContext';
import { SoftPhoneProvider } from '@/contexts/softPhoneContext';
import useAddMessage from '@/hooks/useAddMessage';
import useDismissMessage from '@/hooks/useDismissMessage';
import useThemeSetting from '@/hooks/useThemeSetting';
import Login from '@/pages/Login';
import { ToastMessage } from '@/types/appMessage';
import { DugoutConfig } from '@/types/config';
import type { StaffUserSession } from '@/types/staffSession';
import { UserPermissions } from '@/types/user';
import Toast from '@/ui/components/Toast';
import { AFFECT_MESSAGES_USE_SOFTPHONE, hasPermission } from '@/utils/userPermission';

import DugoutAuthedApp from './DugoutAuthedApp';
import DugoutTelemetry from './DugoutTelemetry';
import useRoleSettingsSync from './hooks/useRoleSettingsSync';
import useUserSettingsSync from './hooks/useUserSettingsSync';
import LoggedOut from './pages/LoggedOut/LoggedOut';
import userRepository from './repositories/UserRepository';
import Spinner from './ui/components/Spinner';
import AxiosRequestInterceptor, { resetLogoutState } from './utils/axiosRequestInterceptor';
import determineServerUrlFromLocation from './utils/determineServerUrlFromLocation';
import mapAxiosErrorMessages from './utils/mapAxiosErrorMessages';

// Create a client
const queryClient = new QueryClient();

const DevTools =
  process.env.NODE_ENV === 'development' ? lazy(() => import('@/components/DevTools')) : null;

/**
 * Headless: keeps the settings store in step with the server. It has to render
 * inside both AppContext (for the host) and QueryClientProvider, which is
 * below where DugoutApp itself runs.
 */
function UserSettingsSync({ enabled }: { enabled: boolean }) {
  useUserSettingsSync({ enabled });
  useRoleSettingsSync({ enabled });
  return null;
}

// Global App Setup
function getDefaultHost(): string {
  const previousHost = localStorage.getItem('dugoutHost');
  if (typeof previousHost === 'string') {
    return previousHost;
  } else {
    return determineServerUrlFromLocation();
  }
}

function loadHomebaseCurrentUser(host: string): StaffUserSession | null {
  const currentUserJson = localStorage.getItem('dugoutCurrentUser');

  if (!currentUserJson) {
    return null;
  }

  const currentUserByHost: { [key: string]: StaffUserSession } = JSON.parse(currentUserJson);

  // Handle Upgrade from non host based storage
  // If there is a id in the root of the object, assume that this is a full StaffUserSession
  if (currentUserByHost?.id) {
    const staffUserSession = currentUserByHost as unknown as StaffUserSession;
    localStorage.setItem('dugoutCurrentUser', JSON.stringify({ [host]: staffUserSession }));

    return staffUserSession.id ? staffUserSession : null;
  }

  const currentUser = currentUserByHost[host] || null;

  // If no ID return null; Prevents error fetching
  // permissions feature
  return currentUser?.id ? currentUser : null;
}

function DugoutApp() {
  const [host, setHost] = useState(getDefaultHost());
  const [currentUser, setCurrentUser] = useState<StaffUserSession | null>(
    loadHomebaseCurrentUser(host)
  );

  const [logoutReason, setLogoutReason] = useState<string | null>(null);
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const [pendingRequests, setPendingRequests] = useState(new Array<string>());
  const { themeSetting, setThemeSetting, resolvedTheme, darkStyle, setDarkStyle } =
    useThemeSetting();

  const addMessage = useAddMessage(setMessages);
  const dismissMessage = useDismissMessage(setMessages);

  const defaultConfig: DugoutConfig = {
    ABLY_API_KEY: '',
    STREAM_API_KEY: '',
    DUAL_DIAGNOSIS_PSYCHIATRY_FOLLOWUP_APPT_TYPE_ID: '',
    kustomer_default_from_email: '',
    MEDICAL_CHECKIN_APPT_TYPE_ID: '',
    PSYCHIATRY_CHECKIN_APPT_TYPE_ID: '',
    PUBLIC_CHANNEL_WEBSOCKET_KEY: '',
    staff_inactivity_timeout: 30,
    ai_provider: '',
    ai_model_name: '',
    softphone_crisis_line_enabled: false,
    message_provider_email: '',
    reverification_vendor: ''
  };

  const [config, setConfig] = useState<DugoutConfig>(defaultConfig);
  const [permissions, setPermissions] = useState<UserPermissions | undefined>(undefined);
  const [loading, setLoading] = useState({
    config: false,
    permissions: false
  });

  function setAndStoreHost(value: string) {
    localStorage.setItem('dugoutHost', value);
    setHost(value);

    return value;
  }

  const appContext: AppContextInterface = {
    addMessage,
    config,
    currentUser,
    resolvedTheme,
    host,
    permissions,
    setCurrentUser: (user) => {
      if (user) {
        resetLogoutState();
      } else {
        setMessages([]);
        queryClient.clear();
      }
      setCurrentUser(user);
    },
    themeSetting,
    setThemeSetting,
    darkStyle,
    setDarkStyle,
    setHost: setAndStoreHost,
    setLogoutReason,
    setPendingRequests,
    pendingRequests
  };

  useEffect(() => {
    const getCurrentUserData = async () => {
      if (currentUser && host) {
        try {
          setLoading((prev) => ({ ...prev, config: true, permissions: true }));
          const response = await userRepository.getCurrentUser({ host });

          if (response) {
            setConfig(response.config);
            setPermissions(response.permissions);
          }
        } catch (error) {
          addMessage(mapAxiosErrorMessages(error as AxiosError));
        } finally {
          setLoading((prev) => ({
            ...prev,
            config: false,
            permissions: false
          }));
        }
      }
    };

    void getCurrentUserData();
  }, [addMessage, host, currentUser]);

  const renderApp = useMemo(() => {
    return (
      <div className="flex flex-col text-base min-h-screen">
        {currentUser && host && config && permissions ? (
          <DugoutAuthedApp
            host={host}
            currentUser={currentUser}
            addMessage={addMessage}
            permissions={permissions}
          />
        ) : !loading.config && !loading.permissions ? (
          logoutReason ? (
            <LoggedOut reason={logoutReason} onDismiss={() => setLogoutReason(null)} />
          ) : (
            <Login />
          )
        ) : (
          <div className="flex-grow flex items-center justify-center">
            <Spinner label="Logging In..." />
          </div>
        )}

        {!!messages.length &&
          messages.map((message, idx) => (
            <Toast
              key={`toast-message-${idx}-${message.id}}`}
              index={idx}
              message={message}
              dismissMessage={() => dismissMessage(message.id)}
            />
          ))}
      </div>
    );
  }, [
    currentUser,
    host,
    config,
    permissions,
    addMessage,
    loading.config,
    loading.permissions,
    messages,
    dismissMessage,
    logoutReason
  ]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => <Fallback error={error} resetError={resetError} />}
      showDialog>
      <InternalMixpanelProvider>
        <DugoutTelemetry currentUser={currentUser} permissions={permissions} messages={messages} />
        <AppContext.Provider value={appContext}>
          <AxiosRequestInterceptor>
            <QueryClientProvider client={queryClient}>
              <UserSettingsSync enabled={!!currentUser?.id} />
              <SoftPhoneProvider
                enabled={hasPermission({
                  permissions,
                  requiredPermission: AFFECT_MESSAGES_USE_SOFTPHONE
                })}
                disabledInEmbed={
                  new URLSearchParams(window.location.search).get('disable_softphone') === 'true'
                }>
                <SidebarLayoutProvider>
                  <CardOrderProvider>
                    <NarrowNavProvider>
                      <PageAnchorsProvider>
                        {renderApp}
                        <ActiveCallOverlay />
                        {DevTools && (
                          <Suspense fallback={null}>
                            <DevTools />
                          </Suspense>
                        )}
                      </PageAnchorsProvider>
                    </NarrowNavProvider>
                  </CardOrderProvider>
                </SidebarLayoutProvider>
              </SoftPhoneProvider>
            </QueryClientProvider>
          </AxiosRequestInterceptor>
        </AppContext.Provider>
      </InternalMixpanelProvider>
    </Sentry.ErrorBoundary>
  );
}

export default DugoutApp;
